/* Farpoint — cart-level shipping insurance
 * ========================================
 *
 * Companion to snippets/fp-cart-insurance.liquid. Read that file's comment
 * first; it explains why insurance stopped being a product option.
 *
 * DIVISION OF LABOUR
 *   Liquid decides. This file acts.
 *   The snippet renders inside `.cart__items`, which the theme replaces
 *   wholesale after every cart mutation, so the row is always re-rendered from
 *   real server-side cart state. This file holds NO copy of that state — it
 *   reads `data-fp-action` off the freshly rendered row and does what it says.
 *   That is deliberate: the last storefront bug on this store came from a
 *   client-side script that believed its own idea of the cart.
 *
 * WHAT IT IS ALLOWED TO WRITE
 *   Exactly one variant — the insurance variant named in the row's
 *   `data-fp-variant-id` — at a quantity of exactly 0 or 1, plus the
 *   `fp_insurance_optout` cart attribute. It never touches another line, never
 *   sets a price, and never writes a line property. Pricing for configured
 *   products remains entirely server-side in the Cart Transform.
 *
 * NOTE ON /cart/update.js
 *   `updates` is keyed by variant id and sets an ABSOLUTE quantity, adding the
 *   line if it is missing and removing it at 0. One call therefore covers add,
 *   remove and the >1 cap, and it carries the attribute change in the same
 *   round trip so the two can never disagree.
 */
(function () {
  "use strict";

  /* Bumped whenever this file changes. The theme keeps its OWN copy of this
     asset and nothing keeps it in sync with the repo — a stale copy has already
     caused a live double-charge once on this store. Check this in the console
     against the repo before debugging anything else. */
  window.FP_CART_INSURANCE_VERSION = "2026-07-31.3";

  var ATTR = "fp_insurance_optout";

  /* An automatic action should converge in one step. If it hasn't after a few,
     something disagrees with us and looping would hammer the cart endpoint —
     stop and leave the cart alone rather than spin.

     The budget is per cart-state, not per page load: `data-fp-sig` changes
     whenever the cart genuinely moves on, which resets the count, so a busy
     shopper still gets an auto-add on every new cart state.

     This guard earned its keep: V23 tried to move insurance to the end of the
     cart by removing and re-adding it, on the assumption a new line lands last.
     It lands FIRST — this cart is newest-first. The action never became
     satisfied, and only this counter stopped it looping. Cart line order is not
     controllable from the storefront; see the snippet's comment. */
  var MAX_AUTO_ACTIONS = 3;
  var autoActions = 0;
  var lastSig = null;

  var busy = false;
  var pending = null;
  var observed = typeof WeakSet === "function" ? new WeakSet() : null;

  ready(function () {
    // Delegated so it survives every drawer rebuild without rebinding.
    document.addEventListener("change", function (e) {
      var box = e.target && e.target.closest && e.target.closest("[data-fp-insurance-toggle]");
      if (box) onToggle(box);
    });

    // The theme's own signals that the cart changed underneath us.
    ["cart:updated", "ajaxProduct:added", "CartDrawer:change", "CartDrawer:open"].forEach(function (name) {
      document.addEventListener(name, schedule);
    });

    schedule();
  });

  /* ---------- reconcile ---------- */

  // Debounced: several of the theme's events fire around a single rebuild, and
  // some fire fractionally BEFORE the markup swap lands.
  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(reconcile, 150);
  }

  function reconcile() {
    watchContainers();

    var row = document.querySelector("[data-fp-insurance]");
    if (!row || busy) return;

    var action = row.getAttribute("data-fp-action");
    if (!action || action === "none") return;

    var sig = row.getAttribute("data-fp-sig");
    if (sig !== lastSig) {
      lastSig = sig;
      autoActions = 0;
    }
    if (autoActions >= MAX_AUTO_ACTIONS) return;

    var id = row.getAttribute("data-fp-variant-id");
    if (!id) return;

    autoActions++;

    if (action === "add") {
      // Pre-checked / opt-out, per Cary 2026-07-31. Only ever reached when the
      // cart holds something else and the customer has not opted out.
      apply(row, id, 1, null);
    } else if (action === "cap") {
      // Somehow more than one. Force it back to a single unit.
      apply(row, id, 1, null);
    } else if (action === "strand") {
      // Insurance is all that's left. Nobody insures a shipment of nothing, and
      // leaving it would let a customer check out paying $4.25 for no goods.
      // Clearing the opt-out too, so the next real item starts fresh.
      apply(row, id, 0, "");
    }
  }

  /* ---------- toggle ---------- */

  function onToggle(box) {
    var row = box.closest("[data-fp-insurance]");
    if (!row || busy) return;

    var id = row.getAttribute("data-fp-variant-id");
    if (!id) return;

    // A deliberate click is not an automatic action, and it should also clear
    // any budget already spent so a later legitimate auto-add still runs.
    autoActions = 0;

    if (box.checked) {
      apply(row, id, 1, "");
    } else {
      // Sticky: without this the next reconcile would helpfully add it straight
      // back and the box would appear to refuse to turn off.
      apply(row, id, 0, "yes");
    }
  }

  /* ---------- cart write ---------- */

  function apply(row, variantId, qty, optout) {
    start(row, qty > 0);
    settle(row, post(write(variantId, qty, optout)));
  }

  function write(variantId, qty, optout) {
    var body = { updates: {}, sections: "cart-ajax" };
    body.updates[variantId] = qty;
    if (optout !== null) {
      var attrs = {};
      attrs[ATTR] = optout;
      body.attributes = attrs;
    }
    return body;
  }

  function start(row, on) {
    busy = true;
    row.classList.add("is-busy");
    if (on) row.classList.add("is-on");
    else row.classList.remove("is-on");
  }

  function settle(row, work) {
    work
      .then(function (cart) {
        updateBubble(cart);
        // Let the theme re-render from the server rather than patching markup
        // ourselves — the rebuilt row carries the authoritative next action.
        document.dispatchEvent(new CustomEvent("cart:build"));
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: cart } }));
      })
      .catch(function (err) {
        // Fail quiet but logged: the next rebuild re-renders the row from real
        // server state, so a failure self-corrects. Never alert — a failed
        // insurance toggle must not stand between the customer and checkout.
        if (window.console) console.error("[fp-cart-insurance]", err);
      })
      .then(function () {
        busy = false;
        row.classList.remove("is-busy");
        schedule();
      });
  }

  function post(body) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(root + "cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw r;
      return r.json();
    });
  }

  /* ---------- header count ---------- */

  // The theme's own cartMarkup() does not refresh this, so the header bubble
  // goes stale after any ajax cart change. Adding a line made that obvious
  // enough to be worth fixing here.
  function updateBubble(cart) {
    if (!cart || typeof cart.item_count !== "number") return;
    document.querySelectorAll(".cart-link__bubble").forEach(function (bubble) {
      var num = bubble.querySelector(".cart-link__bubble-num");
      if (num) num.textContent = cart.item_count;
      bubble.classList.toggle("cart-link__bubble--visible", cart.item_count > 0);
    });
  }

  /* ---------- rebuild detection ---------- */

  // Belt and braces for rebuilds the theme performs without announcing them
  // (CartForm.buildCart fires no event). Scoped to the cart containers, so this
  // is a handful of nodes rather than a subtree observer on the document.
  function watchContainers() {
    if (!observed || typeof MutationObserver !== "function") return;
    document.querySelectorAll("[data-products]").forEach(function (el) {
      if (observed.has(el)) return;
      observed.add(el);
      new MutationObserver(schedule).observe(el, { childList: true });
    });
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }
})();
